import type { TermsResponse } from "@patent-studio/api-types";

/**
 * A component's old name in the attorney's own words.
 *
 * A walk-through is typed text: renaming a component changes its record and
 * its term, and leaves the words that already name it as they were. These
 * read the terms' history to find the old name in such words, and change it
 * only when the attorney asks, so their words are never rewritten unseen.
 */

/** A component's earlier name, and the name it goes by now. */
export interface Rename {
  old: string;
  now: string;
}

const fold = (name: string) => name.trim().toLowerCase();

/**
 * Each component's past names, paired with its name now. A past name another
 * term goes by now is left out: in the words it may well mean that term.
 */
export function renamesOf(view: TermsResponse | undefined): Rename[] {
  if (view === undefined) return [];
  const current = new Set(
    view.groups.flatMap((group) => group.terms.map((term) => fold(term.name))),
  );
  return view.groups
    .filter((group) => group.kind === "components")
    .flatMap((group) => group.terms)
    .flatMap((term) =>
      term.history
        .map((change) => change.name)
        .filter((old) => !current.has(fold(old)))
        .map((old) => ({ old, now: term.name })),
    );
}

/** A run of letters or digits in a text, and where it stands. */
interface Word {
  word: string;
  start: number;
  end: number;
}

const WORD = /[\p{L}\p{N}]+/gu;

function wordsIn(text: string): Word[] {
  return [...text.matchAll(WORD)].map((match) => ({
    word: match[0].toLowerCase(),
    start: match.index,
    end: match.index + match[0].length,
  }));
}

/** What stands between two words, with any run of spaces read as one space. */
const gap = (text: string, from: number, to: number) =>
  text.slice(from, to).replace(/\s+/g, " ");

/**
 * Where `name` stands in `text` as whole words: in any case, with any run of
 * spaces between its words, and anything else between them as the name has it.
 * Word by word, with no pattern built from the name.
 */
function spansOf(text: string, name: string): [number, number][] {
  const words = wordsIn(text);
  const wanted = wordsIn(name);
  if (wanted.length === 0) return [];
  const spans: [number, number][] = [];
  for (let first = 0; first + wanted.length <= words.length; first += 1) {
    const run = words.slice(first, first + wanted.length);
    const same = run.every(
      (word, index) =>
        word.word === wanted[index].word &&
        (index === 0 ||
          gap(text, run[index - 1].end, word.start) ===
            gap(name, wanted[index - 1].end, wanted[index].start)),
    );
    if (same) spans.push([run[0].start, run[run.length - 1].end]);
  }
  return spans;
}

/** Where the old name stands on its own in `text`, not inside the name now. */
function usesOf(text: string, { old, now }: Rename): [number, number][] {
  const inside = spansOf(text, now);
  return spansOf(text, old).filter(
    ([start, end]) => !inside.some(([from, to]) => start >= from && end <= to),
  );
}

/** The renames whose old name the text still uses. */
export function oldNamesIn(text: string, renames: Rename[]): Rename[] {
  return renames.filter((rename) => usesOf(text, rename).length > 0);
}

/** The text with the name now in place of each use of the old one; a use that opened on a capital keeps it. */
export function replaceOldName(text: string, rename: Rename): string {
  let out = text;
  for (const [start, end] of usesOf(text, rename).reverse()) {
    const capital = text[start] !== text[start].toLowerCase();
    const now = capital
      ? rename.now[0].toUpperCase() + rename.now.slice(1)
      : rename.now;
    out = out.slice(0, start) + now + out.slice(end);
  }
  return out;
}

/** What the attorney reads of a rename their words have not caught up with. */
export function oldNameWords({ old, now }: Rename): string {
  return `Uses an old name: "${old}", now "${now}".`;
}
